import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid } from '@mui/material'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { acGetAllPlanGroups, acPostPlanGroup, acPutPlanGroup } from '../../store/actions'
import { PrimaryButton, Alert } from '../default'

import { FormInputText, FormInputDate, FormMultipleSelect, FormCheckbox } from './formInputs'
import { FormValidationCreatePlanGroup } from './formValidations'

/**
 * @description Renders a form for creating or updating a plan group.
 * 
 * @author Maria Eduarda Franke
 * @param {function} props.onClose - The function to close the form.
 * @param {function} props.setShowSnackbar - The function to set the visibility of a snackbar.
 * @param {Object} props.selectedPlanGroup - The selected plan group object.
 * @returns {JSX.Element} The rendered form component.
 */
export const CreatePlanGroupForm = ({ onClose, setShowSnackbar, selectedPlanGroup }) => {

    const defaultValues = {
        name: selectedPlanGroup?.name || '',
        description: selectedPlanGroup?.description || '',
        validUntil: selectedPlanGroup ? dayjs(selectedPlanGroup.valid_until) : null,
        isDefaultTable: selectedPlanGroup?.is_default_table || false,
        plans: (selectedPlanGroup?.plans || []).map((plan) => plan.plan_id) || []
    }

    const dispatch = useDispatch()

    const allPlans = useSelector(state => state.plans.allPlans)

    const isCreatingPlanGroup = useSelector(state => state.planGroups.isCreatingPlanGroup)
    const hasErrorCreatedPlanGroup = useSelector(state => state.planGroups.hasErrorCreatedPlanGroup)

    const isUpdatingPlanGroup = useSelector(state => state.planGroups.isUpdatingPlanGroup)
    const hasErrorUpdatedPlanGroup = useSelector(state => state.planGroups.hasErrorUpdatedPlanGroup)

    const { control, handleSubmit, formState: { errors } } = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(FormValidationCreatePlanGroup)
    })

    const onSubmit = handleSubmit((data) => {
        if (selectedPlanGroup) {
            dispatch(acPutPlanGroup(selectedPlanGroup.plan_group_id, data))
                .then(() => {
                    onClose()
                    setShowSnackbar(true)
                    dispatch(acGetAllPlanGroups())
                        .catch(error => console.error(error))
                })
                .catch(error => console.error(error))
        } else {
            dispatch(acPostPlanGroup(data))
                .then(() => {
                    onClose()
                    setShowSnackbar(true)
                    dispatch(acGetAllPlanGroups())
                        .catch(error => console.error(error))
                })
                .catch(error => console.error(error))
        }
    })

    return (
        <Box
            component='form' onSubmit={onSubmit} noValidate
            display='flex' flexDirection='column' gap={2}
        >

            <DevTool control={control} />

            <FormInputText
                name='name'
                label='Nome'
                control={control}
                errors={errors.name}
                fullWidth
                required
            />

            <FormInputText
                name='description'
                label='Descrição'
                control={control}
                errors={errors.description}
                fullWidth
                multiline
                rows={3}
            />

            <Grid container display='flex' flexDirection='row' alignItems='center'
                spacing={2}
            >
                <Grid item xs={12} sm={6}>
                    <FormInputDate
                        name='validUntil'
                        label='Válido Até'
                        control={control}
                        errors={errors.validUntil}
                        textFieldProps={{ fullWidth: true, required: true }}
                        disablePast
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormCheckbox
                        name='isDefaultTable'
                        label='Setar como grupo padrão?'
                        control={control}
                    />
                </Grid>
            </Grid>

            <FormMultipleSelect
                name='plans'
                label='Planos'
                items={allPlans}
                itemId='plan_id'
                control={control}
                errors={errors.plans}
                fullWidth
                required
            />

            {(hasErrorCreatedPlanGroup || hasErrorUpdatedPlanGroup) && (
                <Alert severity='error' message={`Erro ao ${selectedPlanGroup ? 'criar' : 'alterar'} grupo de planos!`} />
            )}

            <PrimaryButton
                label={selectedPlanGroup ? 'Salvar' : 'Criar Grupo de Planos'}
                loading={isCreatingPlanGroup || isUpdatingPlanGroup}
                type='submit'
                fullWidth
            />

        </Box>
    )

}

CreatePlanGroupForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    setShowSnackbar: PropTypes.func.isRequired,
    selectedPlanGroup: PropTypes.object
}
